import { isNative } from '../native';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { c } from '../theme';
import { Notice, Wordmark } from '../components/Brand';
import { AuthScene, PillChip, PillField, SceneHeadline, SceneLead, SceneLink, STRENGTH_GREEN, VioletCTA } from '../components/AuthScene';
import { api, ApiError, type User } from '../api';

/**
 * Sign In and Sign Up, from the auth-flow handoff: the light scrim over the
 * clip, frosted pill fields and the violet call to action. No social sign-in.
 */

/** Length 8+, an uppercase letter, a digit, a symbol — one point each. */
function strength(pw: string): { label: string; bg: string; fg: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const tones = ['#F5EEDB', c.red, c.magenta, c.violet, STRENGTH_GREEN];
  const labels = ['8+ CHARS', 'WEAK', 'FAIR', 'GOOD', 'STRONG'];
  return { label: labels[score], bg: tones[score], fg: score ? c.cream : 'rgba(31,26,31,0.6)' };
}

export function SignIn({ onUser, initialMode = 'signin' }: { onUser: (u: User) => void; initialMode?: 'signin' | 'signup' }) {
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const signup = mode === 'signup';

  const ready = email.trim().length > 3 && password.length >= 8 && (!signup || name.trim().length > 0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const { user } = signup ? await api.signUp(email.trim(), password, name.trim(), false) : await api.signIn(email.trim(), password);
      onUser(user);
      navigate('/search', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const swap = () => {
    navigate(signup ? '/signin' : '/signup', { replace: true });
    setMode(signup ? 'signin' : 'signup');
    setError(null);
    setNote(null);
  };

  const pw = strength(password);

  return (
    <AuthScene scrim="light">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingTop: 48 }}>
        <div style={{ padding: '0 var(--gx)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Wordmark />
            <button type="button" onClick={() => navigate('/')} style={{ fontSize: 10, letterSpacing: '0.16em', fontWeight: 600, color: 'rgba(31,26,31,0.6)' }}>
              BACK
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 56 }}>
            {signup ? (
              <>
                <SceneHeadline>
                  Let’s get you <span style={{ fontWeight: 700 }}>ready</span>
                </SceneHeadline>
                <SceneLead style={{ maxWidth: 280 }}>One account, every report you run.</SceneLead>
              </>
            ) : (
              <>
                <SceneHeadline>
                  We <span style={{ fontWeight: 700 }}>missed</span> you
                </SceneHeadline>
                <SceneLead>Pick up where you left off. Your reports stay on this account.</SceneLead>
              </>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 34 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {signup && (
                <PillField dot={c.red} type="text" autoComplete="given-name" placeholder="first name" value={name} onChange={(e) => setName(e.target.value)} aria-label="First name" />
              )}
              <PillField dot={c.violet} type="email" autoComplete="email" placeholder="e-mail address" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="E-mail address" />
              <PillField
                dot={c.cyan}
                type="password"
                autoComplete={signup ? 'new-password' : 'current-password'}
                placeholder="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label="Password"
                inputStyle={{ letterSpacing: '0.06em' }}
                trailing={
                  signup ? (
                    password ? <PillChip bg={pw.bg} fg={pw.fg}>{pw.label}</PillChip> : null
                  ) : (
                    <PillChip onClick={() => setNote('Password reset isn’t available yet — contact us and we’ll reset it for you.')}>FORGOT?</PillChip>
                  )
                }
              />
            </div>

            {error && <Notice tone="error">{error}</Notice>}
            {note && <Notice>{note}</Notice>}

            <VioletCTA type="submit" disabled={!ready || busy} style={{ marginTop: 8 }}>
              {busy ? 'one moment…' : signup ? 'create account' : 'sign in'}
            </VioletCTA>

            {signup && (
              <div style={{ fontSize: 10, lineHeight: 1.5, color: 'rgba(31,26,31,0.6)', textAlign: 'center' }}>
                {isNative ? 'Next, you will read and agree to the terms of use.' : 'Next, you will read and sign the pre-release testing agreement.'}
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: signup ? '30px 40px 44px' : '34px 40px 44px' }}>
          <div style={{ fontSize: 10, letterSpacing: '0.14em', fontWeight: 600, color: 'rgba(31,26,31,0.6)' }}>
            {signup ? 'ALREADY WITH US?' : 'NEW TO READYBUY?'}
          </div>
          <SceneLink onClick={swap}>{signup ? 'SIGN IN' : 'CREATE AN ACCOUNT'}</SceneLink>
        </div>
      </form>
    </AuthScene>
  );
}
